import React, { useEffect, useState } from 'react';
import { Button, Input } from 'antd';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { formatNumber, isNumeric } from '../utils/numberUtil';
import Loader from '../Components/Loader';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';
import Container from '../Components/Container';

const WithdrawalPage: React.FC = () => {

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [password, setPassword] = useState("");
    const [balances, setBalances] = useState("");
    const [creditScore, setCreditScore] = useState(0);
    const [withdrawEnable, setWithdrawEnable] = useState(false);
    const [withdrawFailedCount, setWithdrawFailedCount] = useState(0);
    const { t } = useTranslation();

    const submitWithdrawal = async () => {
        if (!withdrawAmount || !password) return;
        if (!isNumeric(withdrawAmount)) return;

        if (!withdrawEnable) {
            Swal.fire({
                title: 'Error!',
                text: t("please_reach_withdraw"),
                icon: 'error',
            })
            getUserInfo();
            return;
        }

        if (creditScore < 100) {
            Swal.fire({
                title: 'Error!',
                text: t("credit_score_not_enough"),
                icon: 'error',
            })
            getUserInfo();
            return;
        }

        let formatted = Number(withdrawAmount).toFixed(2);
        if (Number(formatted) > Number(balances ?? 0)) {
            Swal.fire({
                title: 'Error!',
                text: t("balance_not_enough"),
                icon: 'error',
            })
            getUserInfo();
            return;
        }
        setLoading(true);
        const res = await axiosInstance.post("/protected/submitWithdrawal", {
            amount: formatted,
            withdrawalPassword: password
        })
        setLoading(false);

        try {
            if (res.status === 201) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Withdrawal submitted',
                    icon: 'success',
                }).then(e => {
                    navigate("/account")
                })
            } else {
                if (withdrawFailedCount >= 2) {
                    Swal.fire({
                        title: 'Error!',
                        text: t("withdraw_suspended"),
                        icon: 'error',
                    })
                } else {
                    Swal.fire({
                        title: 'Error!',
                        text: 'Invalid Password',
                        icon: 'error',
                    })
                }
                getUserInfo();
            }
        } catch {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to submit withdrawal',
                icon: 'error',
            })
            getUserInfo();
        }
    }

    const getUserInfo = async () => {
        setLoading(true);
        const res = await axiosInstance.get("/protected/getUserInfo")
        const data = res.data.data;
        if (data) {
            const user = data.user;
            if (!user.isActive) {
                navigate("/login");
                return;
            }
            setBalances(user.walletBalances);
            setWithdrawEnable(user.withdrawEnable);
            setCreditScore(user.creditScore);
            setWithdrawFailedCount(user.withdrawFailedCount ?? 0)
        } else {
            navigate("/login")
        }
        setTimeout(() => {
            setLoading(false);
        }, 1500);

    }

    // Mount-only fetch; see AccountPage for why the dependency is omitted.
    useEffect(() => {
        getUserInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <AppShell>
            {loading && <Loader />}
            <Container>
                {/* At lg this becomes a 2-column grid: balance summary left (1fr),
                    the withdrawal form right, capped at 560px so the amount/password
                    inputs don't stretch full-width. Below lg the `contents` wrapper
                    dissolves its own box, so its three children fall back to being
                    direct flex items of this div, spaced by the existing (unprefixed)
                    gap-5 — mobile markup is byte-identical to before this wrapper
                    existed. */}
                <div className='p-6 flex flex-col gap-5 lg:grid lg:grid-cols-[1fr_560px] lg:gap-10 lg:items-start'>
                    <div className='bg-brand-800 min-h-[120px] text-white flex flex-col p-8' style={{
                        borderRadius: ".13333rem 2.8rem .13333rem .13333rem"
                    }}>
                        <div>{t("digital_balance")}</div>
                        <div>RM <span className='text-[30px] font-bold'>{formatNumber(Number(balances ?? 0))}</span></div>
                    </div>

                    <div className='contents lg:flex lg:flex-col lg:gap-5'>
                        <div className='bg-white p-2'>
                            <div className='text-brand-600 text-[14px]'>{t('total_withdraw')}</div>
                            <div className='flex border-b-[0.5px] border-solid border-brand-600'>
                                <div className='text-[30px] font-bold m-auto'>RM</div>
                                <div className='flex-1'>
                                    <Input
                                        value={withdrawAmount}
                                        step="0.01"
                                        onChange={(e) => setWithdrawAmount(e.target.value)}
                                        className='text-left text-[30px] font-bold p-0 pl-1 leading-[0]'
                                        type="number" size="large" variant="borderless" />
                                </div>
                            </div>
                            <div className='text-brand-600 text-[14px]'>{t("minimum_withdrawal_is")} RM 30</div>
                        </div>

                        <div className='flex bg-white rounded-md border-2 border-solid text-[16px]'>
                            <p className='px-[11px] py-[4px] m-auto min-w-fit text-[14px]'>{t("withdrawal_password")}</p>
                            <Input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className='text-right'
                                type="password" size="large" placeholder={t("type_here")} variant="borderless" />
                        </div>

                        <div className='w-full flex'>
                            <Button
                                disabled={!password || !isNumeric(withdrawAmount) || Number(withdrawAmount) < 30}
                                className='w-[80%] text-white m-auto bg-brand-800'
                                size='large' type="primary"
                                onClick={() => submitWithdrawal()}
                            >
                                {t("confirm")}
                            </Button>
                        </div>
                    </div>
                </div>
            </Container>
        </AppShell>
    );
}

export default WithdrawalPage;